import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { fetchReportTemplate } from "app/services/report_templates/reportNoteTemplateService";

import {
  changeAlertStatus,
  changeAnticoagStatus,
  updateAlertStatus,
  downloadReportFile,
  fetchBillingHistory,
  previousAlerts,
  previousReports,
  reportById,
  saveReportTask,
  sendOruDft,
  tasksByReportId,
  updateNurseFlag,
  upsertEHRNotes,
  sendOruForIntergyAccount,
  sendReportToSFTPInterface,
  updateLaaoDevice,
  fetchReportNoteSuggestions,
  updateIDTF,
  updateAuditStatus,
} from "app/services/transmission/report";
import { getFileName } from "app/utils/file";
import { BASE_URL } from "static/constants";

export const transmissionDetailApi = createApi({
  reducerPath: "transmissionApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  tagTypes: ["reportDetail", "billingHistory", "reportTasks"],
  keepUnusedDataFor: 2,
  endpoints: ({ query, mutation }) => ({
    reportDetail: query({
      queryFn: ({ reportId, isReportDetailModal }) => {
        return {
          data: reportById(reportId, isReportDetailModal),
        };
      },
      keepUnusedDataFor: 0,
      providesTags: (reportId) => ["reportDetail", reportId],
    }),
    getPreviousAlerts: query({
      queryFn: ({ reportId, alert, date, isPreviousAlert }) => {
        return {
          data: previousAlerts(reportId, alert, date, isPreviousAlert),
        };
      },
    }),
    getPreviousReports: query({
      queryFn: ({ reportId, isReport }) => {
        return {
          data: previousReports(reportId, isReport),
        };
      },
    }),
    getReportTemplate: query({
      queryFn: ({ reportId }) => {
        return {
          data: fetchReportTemplate(reportId),
        };
      },
      keepUnusedDataFor: 5,
    }),
    getReportBillingHistory: query({
      queryFn: (reportId) => {
        return {
          data: fetchBillingHistory(reportId),
        };
      },
      keepUnusedDataFor: 0,
      providesTags: (reportId) => ["billingHistory", reportId],
    }),
    getReportTasks: query({
      queryFn: ({ reportId, account, showAuditedReport }) => {
        return {
          data: tasksByReportId(reportId, account, showAuditedReport),
        };
      },
      keepUnusedDataFor: 2,
      providesTags: ["reportTasks"],
    }),
    setNurseFLag: mutation({
      queryFn: ({ reportId, status, account }) => {
        return { data: updateNurseFlag({ reportId, status, account }) };
      },
    }),
    setAlertStatus: mutation({
      queryFn: ({ reportId, status }) => {
        return { data: changeAlertStatus(reportId, status) };
      },
    }),

    setAnticoagStatus: mutation({
      queryFn: ({ patientId, status, account, refusesOAC }) => {
        return {
          data: changeAnticoagStatus(patientId, status, account, refusesOAC),
        };
      },
    }),
    setUpdateAlertStatus: mutation({
      queryFn: ({ reportId, status }) => {
        return { data: updateAlertStatus(reportId, status) };
      },
    }),
    handleOruDft: mutation({
      queryFn: (reportId) => {
        return { data: sendOruDft(reportId) };
      },
    }),
    handleOruForIntergyAccount: mutation({
      queryFn: (reportId) => {
        return { data: sendOruForIntergyAccount(reportId) };
      },
    }),
    sendReportToSFTPInterface: mutation({
      queryFn: (reportId) => {
        return { data: sendReportToSFTPInterface(reportId) };
      },
    }),
    updateReportTask: mutation({
      queryFn: ({
        task,
        reportId,
        patientId,
        account,
        isEditTask,
        patientSerialNumber,
        patientName,
      }) => {
        return {
          data: saveReportTask(
            task,
            reportId,
            patientId,
            account,
            isEditTask,
            patientSerialNumber,
            patientName
          ),
        };
      },
      invalidatesTags: ["reportTasks"],
    }),
    downloadReport: mutation({
      queryFn: ({ reportId, filters }) => {
        return downloadReportFile(reportId, filters).then((response) => {
          const file = new Blob([response.data], {
            type: "application/pdf",
          });
          const fileURL = URL.createObjectURL(file);
          const fileName = getFileName(response);

          var a = document.createElement("a");

          document.body.appendChild(a);
          a.style = "display:none";

          a.href = fileURL;
          a.download = fileName;
          a.click();

          a.remove();
          return { data: response };
        });
      },
    }),
    upsertEHRNotes: mutation({
      queryFn: ({ reportId, payload }) => {
        return { data: upsertEHRNotes(reportId, payload) };
      },
      invalidatesTags: ["ehrNotes"],
    }),
    updateLaaoDevice: mutation({
      queryFn: ({ patientId, status }) => {
        return { data: updateLaaoDevice(patientId, status) };
      },
    }),
    updateAuditStatus: mutation({
      queryFn: ({ reportId, status }) => {
        return { data: updateAuditStatus(reportId, status) };
      },
    }),
    getReportNoteSuggestions: query({
      queryFn: ({ reportId, noteSuggestionEnabled }) => {
        return {
          data: fetchReportNoteSuggestions(reportId, noteSuggestionEnabled),
        };
      },
    }),
    updateIDTF: mutation({
      queryFn: ({ patientId, status }) => {
        return { data: updateIDTF(patientId, status) };
      },
    }),
  }),
  cacheTime: 5000,
});

export const {
  useReportDetailQuery,
  useGetPreviousReportsQuery,
  useGetPreviousAlertsQuery,
  useGetReportTemplateQuery,
  useGetReportBillingHistoryQuery,
  useGetReportTasksQuery,
  useSetNurseFLagMutation,
  useSetAnticoagStatusMutation,
  useSetAlertStatusMutation,
  useSetUpdateAlertStatusMutation,
  useHandleOruDftMutation,
  useUpdateReportTaskMutation,
  useDownloadReportMutation,
  useUpsertEHRNotesMutation,
  useHandleOruForIntergyAccountMutation,
  useSendReportToSFTPInterfaceMutation,
  useUpdateLaaoDeviceMutation,
  useGetReportNoteSuggestionsQuery,
  useUpdateIDTFMutation,
  useUpdateAuditStatusMutation,
} = transmissionDetailApi;
